import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/SimpleAuthContext';
import axios from 'axios';
import { canAccessAnalytics } from '../utils/accessControl';
import './AnalyticsDashboard.css';

// Icons
import {
  ChartBarIcon,
  EyeIcon,
  HeartIcon,
  DocumentTextIcon,
  TrendingUpIcon,
  MapPinIcon,
  CalendarIcon,
  UsersIcon
} from '@heroicons/react/24/outline';

// Configure axios for this component
axios.defaults.baseURL = 'http://localhost:8000';

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30'); // days
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    console.log('Analytics Dashboard - User check:', {
      user: user,
      is_staff: user?.is_staff,
      is_superuser: user?.is_superuser,
      role: user?.role
    });

    if (!user) {
      setError('Please log in to access analytics.');
      setLoading(false);
      return;
    }

    if (!canAccessAnalytics(user)) {
      setError(`Access denied. Privileged access is required. Current role: ${user.role || 'unknown'}, is_staff: ${user.is_staff ? 'true' : 'false'}`);
      setLoading(false);
      return;
    }

    fetchDashboardData();
  }, [user, dateRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem('access_token') || localStorage.getItem('token');
      const hasJwtToken = Boolean(localStorage.getItem('access_token'));
      const authHeader = accessToken
        ? (hasJwtToken ? `Bearer ${accessToken}` : `Token ${accessToken}`)
        : null;
      const headers = authHeader ? { Authorization: authHeader } : {};
      
      const response = await axios.get('/api/analytics/dashboard/', {
        headers,
        withCredentials: true,
        params: {
          days: dateRange
        }
      });

      setDashboardData(response.data);
    } catch (err) {
      console.error('Analytics API error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);

      if (err.response?.status === 403) {
        setError('Access denied. Your account does not have analytics privileges.');
      } else if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else {
        setError(`Failed to load analytics data: ${err.response?.data?.error || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseAnalytics = async (courseId) => {
    try {
      const accessToken = localStorage.getItem('access_token') || localStorage.getItem('token');
      const hasJwtToken = Boolean(localStorage.getItem('access_token'));
      const authHeader = accessToken
        ? (hasJwtToken ? `Bearer ${accessToken}` : `Token ${accessToken}`)
        : null;
      const headers = authHeader ? { Authorization: authHeader } : {};
      
      const response = await axios.get(`/api/analytics/course/${courseId}/`, {
        headers,
        withCredentials: true,
        params: {
          days: dateRange
        }
      });

      setSelectedCourse(response.data);
    } catch (err) {
      console.error('Course analytics error:', err);
    }
  };

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-header">
        <h1>Course Analytics Dashboard</h1>
        <p>GCRA Training Program Insights</p>
        
        <div className="dashboard-controls">
          <div className="date-range-selector">
            <CalendarIcon className="icon-sm" />
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="date-range-select"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>
      </div>

      {dashboardData && (
        <>
          {/* Overview Metrics */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">
                <EyeIcon className="icon-lg" />
              </div>
              <div className="metric-content">
                <h3>Total Page Views</h3>
                <p className="metric-value">{dashboardData.total_page_views.toLocaleString()}</p>
                <span className="metric-label">Course detail pages</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">
                <UsersIcon className="icon-lg" />
              </div>
              <div className="metric-content">
                <h3>Unique Visitors</h3>
                <p className="metric-value">{dashboardData.unique_visitors.toLocaleString()}</p>
                <span className="metric-label">Individual users</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">
                <HeartIcon className="icon-lg" />
              </div>
              <div className="metric-content">
                <h3>Wishlist Items</h3>
                <p className="metric-value">{dashboardData.total_wishlist_items.toLocaleString()}</p>
                <span className="metric-label">Courses saved</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">
                <DocumentTextIcon className="icon-lg" />
              </div>
              <div className="metric-content">
                <h3>Applications</h3>
                <p className="metric-value">{dashboardData.total_applications.toLocaleString()}</p>
                <span className="metric-label">Course applications</span>
              </div>
            </div>
          </div>

          {/* Conversion Rates */}
          <div className="conversion-section">
            <h2>Conversion Rates</h2>
            <div className="conversion-grid">
              <div className="conversion-card">
                <h3>View → Wishlist</h3>
                <p className="conversion-rate">{dashboardData.conversion_rates.view_to_wishlist}%</p>
              </div>
              <div className="conversion-card">
                <h3>View → Application</h3>
                <p className="conversion-rate">{dashboardData.conversion_rates.view_to_application}%</p>
              </div>
              <div className="conversion-card">
                <h3>Wishlist → Application</h3>
                <p className="conversion-rate">{dashboardData.conversion_rates.wishlist_to_application}%</p>
              </div>
            </div>
          </div>

          {/* Most Popular Courses */}
          <div className="popular-courses-section">
            <h2>Most Popular Courses</h2>
            <div className="courses-table">
              <table>
                <thead>
                  <tr>
                    <th>Course Name</th>
                    <th>Campus</th>
                    <th>Views</th>
                    <th>Wishlist</th>
                    <th>Applications</th>
                    <th>Conversion Rate</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.most_popular_courses.map((course, index) => (
                    <tr key={course.course_id}>
                      <td>
                        <div className="course-name">
                          <span className="rank">#{index + 1}</span>
                          {course.course_name}
                        </div>
                      </td>
                      <td>{course.course_campus}</td>
                      <td>{course.total_views}</td>
                      <td>{course.wishlist_count}</td>
                      <td>{course.application_count}</td>
                      <td>{course.view_to_application_rate}%</td>
                      <td>
                        <button 
                          className="btn-view-details"
                          onClick={() => fetchCourseAnalytics(course.course_id)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Geographic Breakdown */}
          {dashboardData.geographic_breakdown.length > 0 && (
            <div className="geographic-section">
              <h2>Geographic Distribution</h2>
              <div className="geographic-grid">
                {dashboardData.geographic_breakdown.map((location, index) => (
                  <div key={index} className="geographic-card">
                    <div className="geographic-header">
                      <MapPinIcon className="icon-sm" />
                      <h3>{location.province}</h3>
                    </div>
                    <p className="corridor">{location.economic_corridor}</p>
                    <div className="geographic-stats">
                      <div className="stat">
                        <span className="stat-value">{location.total_views}</span>
                        <span className="stat-label">Views</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">{location.unique_users}</span>
                        <span className="stat-label">Users</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Course Detail Modal */}
          {selectedCourse && (
            <div className="course-detail-modal">
              <div className="modal-content">
                <div className="modal-header">
                  <h2>{selectedCourse.course_name}</h2>
                  <button 
                    className="modal-close"
                    onClick={() => setSelectedCourse(null)}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="course-metrics">
                    <div className="metric">
                      <span className="metric-label">Total Views</span>
                      <span className="metric-value">{selectedCourse.total_views}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Unique Visitors</span>
                      <span className="metric-value">{selectedCourse.unique_visitors}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Avg. Duration</span>
                      <span className="metric-value">{Math.floor(selectedCourse.average_duration_seconds / 60)}m</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Avg. Scroll Depth</span>
                      <span className="metric-value">{selectedCourse.average_scroll_depth}%</span>
                    </div>
                  </div>
                  
                  <div className="engagement-metrics">
                    <h3>Section Engagement</h3>
                    <div className="engagement-grid">
                      <div className="engagement-item">
                        <span>Modules Viewed</span>
                        <span>{selectedCourse.modules_viewed}</span>
                      </div>
                      <div className="engagement-item">
                        <span>Requirements Viewed</span>
                        <span>{selectedCourse.requirements_viewed}</span>
                      </div>
                      <div className="engagement-item">
                        <span>Careers Viewed</span>
                        <span>{selectedCourse.careers_viewed}</span>
                      </div>
                      <div className="engagement-item">
                        <span>SAQA Clicks</span>
                        <span>{selectedCourse.saqa_clicks}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
